export interface TrackingEventData {
  action: string;
  details: Record<string, any>;
  device: {
    browser: string;
    os: string;
    type: string;
  };
  eventName: string;
  eventType: string;
  network: {
    connectionType: string;
    ip: string;
  };
  page: {
    pageId: string;
    url: string;
  };
  priority: string;
  sessionId: string;
  studentId: string;
  timestamp: string;
  version: string;
  widget: string;
  performance: {
    loadTime: number;
    connectionSpeed: string;
    connectionStability: number;
  };
  userId?: any;
}

export interface TrackingConfig {
  apiEndpoint: string;
  batchSize: number;
  flushInterval: number;
}

interface WorkerMessage {
  type: 'init' | 'track';
  config?: TrackingConfig;
  sessionId?: string;
  event?: TrackingEventData;
}

export class TrackingService {
  private worker: Worker;
  private sessionId: string;
  private ipPromise: Promise<string>;

  constructor(config: TrackingConfig) {
    this.sessionId = this.generateSessionId();
    this.ipPromise = this.fetchIPAddress();    
    this.worker = new Worker(new URL('./worker/tracking-worker.ts', import.meta.url), {
      type: 'module'
    });
    this.worker.postMessage({
      type: 'init',
      config,
      sessionId: this.sessionId
    } as WorkerMessage);
  }

  private async fetchIPAddress(): Promise<string> {
    const ipServices = ['https://api.ipify.org?format=json', 'https://ipapi.co/ip/', 'https://api.ip.sb/ip'];

    return new Promise(resolve => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve('unknown');
        }
      }, 2000);

      ipServices.forEach(async service => {
        if (resolved) return;

        try {
          const controller = new AbortController();
          const serviceTimeout = setTimeout(() => controller.abort(), 1000);

          const response = await fetch(service, {
            signal: controller.signal
          });

          clearTimeout(serviceTimeout);

          if (response.ok && !resolved) {
            const data = await response.text();
            const ip = service.includes('json') ? JSON.parse(data).ip : data.trim();

            if (ip && !resolved) {
              resolved = true;
              clearTimeout(timeout);
              resolve(ip);
            }
          }
        } catch (error) {}
      });
    });
  }

  private getDeviceInfo() {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';
    let type = 'desktop';

    if (userAgent.includes('Edg/')) browser = 'Edge';
    else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) browser = 'Chrome';
    else if (userAgent.includes('Firefox/')) browser = 'Firefox';
    else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) browser = 'Safari';
    else if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) browser = 'Opera';

    if (userAgent.includes('Windows NT')) os = 'Windows';
    else if (userAgent.includes('Mac OS X')) os = 'macOS';
    else if (userAgent.includes('Linux') && !userAgent.includes('Android')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';
    else if (userAgent.includes('CrOS')) os = 'Chrome OS';

    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i.test(userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)|Tablet|PlayBook|Silk/i.test(userAgent);

    if (isMobile && !isTablet) {
      type = 'mobile';
    } else if (isTablet) {
      type = 'tablet';
    } else {
      type = 'desktop';
    }

    if (type === 'desktop' && window.screen) {
      const screenWidth = window.screen.width;
      if (screenWidth <= 768) {
        type = 'mobile';
      } else if (screenWidth <= 1024) {
        type = 'tablet';
      }
    }

    return {
      browser,
      os,
      type
    };
  }

  private async getNetworkInfo() {
    let ip = 'unknown';
    try {
      ip = await Promise.race([this.ipPromise, new Promise<string>(resolve => setTimeout(() => resolve('unknown'), 50))]);
    } catch {
      ip = 'unknown';
    }

    return {
      connectionType: (navigator as any).connection?.effectiveType || 'unknown',
      ip
    };
  }

  private getPageInfo() {
    const pathname = window.location.pathname;
    return {
      pageId: pathname === '/' ? 'Dashboard' : pathname.replace('/', '').split('/')[0] || '/',
      url: pathname
    };
  }

  private getUserInfo() {
    return {
      studentId: localStorage.getItem('studentId') || sessionStorage.getItem('studentId') || ''
    };
  }

  public async recordEvent(
  action: string,
  eventName: string,
  eventType: string,
  widget: string,
  details: Record<string, any> = {},
  priority: string = '2'
): Promise<TrackingEventData> {
  if (import.meta.env.VITE_ENABLE_TRACKING === 'false') return {} as TrackingEventData;

  const userInfo = this.getUserInfo();

  const eventData: TrackingEventData = {
    action,
    details: {
      ...details,
      download: details.download || 'false',
      navigationPath: details.navigationPath || window.location.pathname
    },
    device: this.getDeviceInfo(),
    eventName,
    eventType,
    network: await this.getNetworkInfo(),
    page: this.getPageInfo(),
    performance: this.getPerformanceInfo(),
    priority,
    sessionId: this.sessionId,
    studentId: userInfo.studentId,
    timestamp: new Date().toISOString(),
    version: import.meta.env.VITE_VERSION,
    widget
  };

  if (import.meta.env.VITE_ENV == 'development') {
  } else {
    this.worker.postMessage({
      type: 'track',
      event: eventData
    } as WorkerMessage);
  }
  
  return eventData; 
}

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private getPerformanceInfo() {
    const connection = (navigator as any).connection;

    return {
      loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart || 0,
      connectionSpeed: connection?.downlink ? `${connection.downlink} Mbps` : 'unknown',
      connectionStability: connection?.rtt || 0
    };
  }

  public destroy(): void {
    this.worker.terminate();
  }
}

export const trackingService = new TrackingService({
  apiEndpoint: import.meta.env.VITE_EVENT_TRACKING_URL,
  batchSize: 2,
  flushInterval: 5000
});
